import { AnyAction } from 'redux'
import {
  startLoading,
  stopLoading,
  LoadingTypes,
  DefaultSuccesrOnSubmitData,
  freeTrialDeactivateRequestSuccess,
  freeTrialDeactivateRequestUnsuccess,
  freeTrialOfferIdPatchRequestSuccess,
  freeTrialOfferIdPatchRequestUnsuccess,
  RequestFailureMessage,
  actionTypeWrapper,
  errorLogger,
} from '@labrav/common'
import {
  CloudMarketplace,
  DeactivateFreeTrialParams,
  EnableConcurrentForBuyerArgs,
  FlyoutFunction,
  FreeTrialStatus,
  GetProductInfoDetailArgs,
  Product,
  ProductListingMutationArgs,
  UpdateProductListingDataArgs,
} from './types'
import { camelize, snakeize } from 'casing'
import {
  patchFreeTrial,
  patchProductListingData,
  deleteProductListing,
  publishProductListing,
  unpublishProductListing,
  enableConcurrentForBuyerApi,
} from '../../api/marketplace/index'
import { getApiErrorData } from '../../utils/apiErrorResponse'
import { HardOptSuccessMessage } from '../../common/utils/messagesContants'
import { isEmpty } from 'lodash'
import { AppDispatch, RootState } from '../../store/types'
import {
  getFlyOutProductFormDataByCloudBase,
  getProductsListByCloud,
  getProductsListV2ByCloud,
} from './helper/cloudBaseAPICalls'
import {
  clearSelectedProductIfNeeded,
  dispatchProductListingError,
  dispatchProductListingSuccess,
  dispatchProductsAndFreeTier,
} from './helper/productListingDispatch'
import {
  DEFAULT_PRODUCTS_V2_PAGE_SIZE,
  toProductsV2List,
} from './helper/productsV2Pagination'
import {
  clearProductsListing,
  setProductDetail,
  setProducts,
  setProductsV2,
  setProductsWithListingData,
} from './actionCreators'

export {
  setProducts,
  setProductsWithListingData,
  setProductsV2,
  setFreeTierData,
  clearProductsListing,
  updateFreeTrialOfferId,
  setSelectedProductOnListing,
  setProductDetail,
  clearProductDetail,
} from './actionCreators'

export const getProducts =
  (
    partnerId: string,
    cloudMarketplace: CloudMarketplace,
    shouldLoad = true,
    preserveSelectedProduct = false
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    if (shouldLoad) {
      dispatch(startLoading(LoadingTypes.GENERAL)) //TODO: remove general loader
      dispatch(startLoading(LoadingTypes.FETCH_PRODUCTS))
    }
    clearSelectedProductIfNeeded({
      dispatch,
      getState,
      cloudMarketplace,
      preserveSelectedProduct,
    })
    try {
      const response = await getProductsListByCloud(partnerId, cloudMarketplace)
      if (response?.data) {
        dispatchProductsAndFreeTier({
          dispatch,
          cloudMarketplace,
          data: response.data,
          setProductsAction: setProducts,
        })
      }
    } catch (error: unknown) {
      dispatchProductListingError({
        dispatch,
        getState,
        error,
        defaultMessage: RequestFailureMessage,
      })
    } finally {
      if (shouldLoad) {
        dispatch(stopLoading(LoadingTypes.GENERAL))
        dispatch(stopLoading(LoadingTypes.FETCH_PRODUCTS))
      }
    }
  }

export type GetProductsV2Options = {
  pageNumber?: number
  pageSize?: number
}

export const getProductsV2 =
  (
    partnerId: string,
    cloudMarketplace: CloudMarketplace,
    shouldLoad = true,
    preserveSelectedProduct = false,
    options: GetProductsV2Options = {}
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    if (shouldLoad) {
      dispatch(startLoading(LoadingTypes.FETCH_PRODUCTS_V2))
    }
    clearSelectedProductIfNeeded({
      dispatch,
      getState,
      cloudMarketplace,
      preserveSelectedProduct,
    })
    const requestedPageNumber = options.pageNumber ?? 1
    const requestedPageSize =
      options.pageSize ?? DEFAULT_PRODUCTS_V2_PAGE_SIZE
    const pageNumber = cloudMarketplace === 'AWS' ? requestedPageNumber : 1
    try {
      const response = await getProductsListV2ByCloud(
        partnerId,
        cloudMarketplace,
        undefined,
        requestedPageSize,
        pageNumber
      )
      if (response?.data) {
        const productsV2 = toProductsV2List(response.data, {
          pageNumber,
          pageSize: requestedPageSize,
        })
        dispatchProductsAndFreeTier({
          dispatch,
          cloudMarketplace,
          data: {
            ...response.data,
            products: {
              rows: productsV2.rows,
              page_number: productsV2.pageNumber,
              page_size: productsV2.pageSize,
              has_next_page: productsV2.hasNextPage,
              ...(productsV2.totalCount != null
                ? { total_count: productsV2.totalCount }
                : {}),
            },
          },
          setProductsAction: setProductsV2,
        })
      }
    } catch (error: unknown) {
      dispatchProductListingError({
        dispatch,
        getState,
        error,
        defaultMessage: RequestFailureMessage,
      })
    } finally {
      if (shouldLoad) {
        dispatch(stopLoading(LoadingTypes.FETCH_PRODUCTS_V2))
      }
    }
  }

export const getProductsWithListing =
  (
    partnerId: string,
    cloudMarketplace: CloudMarketplace,
    functionType?: FlyoutFunction
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.GENERAL))
    clearSelectedProductIfNeeded({ dispatch, getState, cloudMarketplace })
    try {
      const response = await getProductsListByCloud(
        partnerId,
        cloudMarketplace,
        'listing_data'
      )
      if (response?.data) {
        dispatch(
          actionTypeWrapper(
            cloudMarketplace,
            setProductsWithListingData(camelize(response.data))
          )
        )
      }
    } catch (error: unknown) {
      dispatchProductListingError({
        dispatch,
        getState,
        error,
        defaultMessage: RequestFailureMessage,
      })
    } finally {
      dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

export const patchFreeTrialOffer =
  (productId: string, partnerId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.GENERAL))
    try {
      const currentSelectedCloudMarketplace =
        getState().productListingFilters.cloudMarketplace
      const productsRows =
        getState().productsListing[currentSelectedCloudMarketplace]?.products
          ?.rows ?? []
      const product =
        productsRows.find(row => row.productId === productId) || ({} as Product)
      const hasCloudFreeTrialOfferId = !isEmpty(
        product?.freeTrial?.cloudFreeTrialOfferId
      )
      const dataToSend = {
        freeTrialEnabled: hasCloudFreeTrialOfferId,
        cloudFreeTrialOfferId: product?.freeTrial?.cloudFreeTrialOfferId || '',
        freeTrialStatus: hasCloudFreeTrialOfferId
          ? FreeTrialStatus.FREE_TRIAL_CREATED
          : FreeTrialStatus.FREE_TRIAL_DEACTIVATED,
        productId,
        freeTrialEnabledAt: new Date().toISOString(),
        cloudMarketplace: product?.cloudMarketplace,
      }
      await patchFreeTrial(
        partnerId,
        snakeize(dataToSend),
        product?.freeTrial?.labraFreeTrialId || ''
      )
      dispatchProductListingSuccess(
        dispatch,
        freeTrialOfferIdPatchRequestSuccess
      )
    } catch (error: unknown) {
      dispatchProductListingError({
        dispatch,
        getState,
        error,
        defaultMessage: freeTrialOfferIdPatchRequestUnsuccess,
      })
    } finally {
      dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

export const deactivateFreeTrial =
  ({
    labraFreeTrialId,
    partnerId,
    productId,
    cloudMarketplace,
  }: DeactivateFreeTrialParams) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.GENERAL))
    try {
      await patchFreeTrial(
        partnerId,
        snakeize({
          productId,
          cloudMarketplace,
          freeTrialStatus: 'FREE_TRIAL_DEACTIVATION_IN_PROGRESS',
        }),
        labraFreeTrialId
      )
      await dispatch(
        actionTypeWrapper(
          getState().productListingFilters.cloudMarketplace,
          clearProductsListing()
        )
      )
      dispatchProductListingSuccess(dispatch, freeTrialDeactivateRequestSuccess)
    } catch (error: unknown) {
      dispatchProductListingError({
        dispatch,
        getState,
        error,
        defaultMessage: freeTrialDeactivateRequestUnsuccess,
      })
    } finally {
      dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

export const updateProductListingData =
  ({
    partnerId,
    productId,
    data,
    alertMessages,
    handleToggleChecked,
  }: UpdateProductListingDataArgs) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.GENERAL))
    try {
      const { cloudMarketplace } = getState().productListingFilters
      await patchProductListingData(partnerId, productId, snakeize(data))
      dispatchProductListingSuccess(
        dispatch,
        alertMessages?.success || DefaultSuccesrOnSubmitData
      )
      await dispatch(
        getProducts(partnerId, cloudMarketplace) as unknown as AnyAction
      )
    } catch (error: unknown) {
      dispatchProductListingError({
        dispatch,
        getState,
        error,
        defaultMessage: alertMessages?.error || RequestFailureMessage,
      })
      handleToggleChecked?.()
    } finally {
      dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

export const enableConcurrentForBuyer =
  ({
    partnerId,
    productId,
    accountIds,
    hardOpt,
    callBack,
  }: EnableConcurrentForBuyerArgs) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      await enableConcurrentForBuyerApi({
        accountId: partnerId,
        productId,
        accountIds,
        hardOpt,
      })
      dispatchProductListingSuccess(
        dispatch,
        hardOpt ? HardOptSuccessMessage : DefaultSuccesrOnSubmitData
      )
      await callBack?.({ success: true })
    } catch (error: unknown) {
      await callBack?.({ success: false })
      dispatchProductListingError({
        dispatch,
        getState,
        error,
        defaultMessage: RequestFailureMessage,
      })
    }
  }

export const publishProductListingData =
  ({ partnerId, productId, alertMessages }: ProductListingMutationArgs) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.GENERAL))
    try {
      await publishProductListing(partnerId, productId, {
        status: 'AWS-testing',
      })
      dispatchProductListingSuccess(
        dispatch,
        alertMessages?.success || DefaultSuccesrOnSubmitData
      )
    } catch (error: unknown) {
      dispatchProductListingError({
        dispatch,
        getState,
        error,
        defaultMessage: alertMessages?.error || RequestFailureMessage,
      })
    } finally {
      dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

export const unpublishProductListingData =
  ({ partnerId, productId, alertMessages }: ProductListingMutationArgs) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.GENERAL))
    try {
      await unpublishProductListing(partnerId, productId, {
        status: 'Restricted',
      })
      dispatchProductListingSuccess(
        dispatch,
        alertMessages?.success || DefaultSuccesrOnSubmitData
      )
    } catch (error: unknown) {
      dispatchProductListingError({
        dispatch,
        getState,
        error,
        defaultMessage: alertMessages?.error || RequestFailureMessage,
      })
    } finally {
      dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

export const deleteProductListingData =
  ({ partnerId, productId, alertMessages }: ProductListingMutationArgs) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.GENERAL))
    try {
      await deleteProductListing(partnerId, productId)
      dispatchProductListingSuccess(
        dispatch,
        alertMessages?.success || DefaultSuccesrOnSubmitData
      )
    } catch (error: unknown) {
      dispatchProductListingError({
        dispatch,
        getState,
        error,
        defaultMessage: alertMessages?.error || RequestFailureMessage,
      })
      throw new Error('Failed to delete the product')
    } finally {
      dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

export const getProductInfoDetail =
  ({ accountId, cloudMarketplace, productId }: GetProductInfoDetailArgs) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.FLYOUT_PRODUCT_DETAIL))
    try {
      const response = await getFlyOutProductFormDataByCloudBase({
        accountId,
        productId,
        cloudMarketplace,
      })
      if (response?.data) {
        dispatch(
          actionTypeWrapper(
            cloudMarketplace,
            setProductDetail(camelize(response.data)?.products?.rows[0])
          )
        )
      }
    } catch (error: unknown) {
      const errorData = getApiErrorData(error)
      errorLogger({ globalState: getState() })(
        error as Error,
        errorData?.track_id
      )
    } finally {
      dispatch(stopLoading(LoadingTypes.FLYOUT_PRODUCT_DETAIL))
    }
  }
